import type { Locale } from './locales';

/**
 * TND formatting (PDF §27, DEV §13): "affichage avec le nombre de décimales
 * usuel (millimes)" — the Tunisian dinar is subdivided into 1000 millimes,
 * not 100, so every displayed amount carries THREE decimal places
 * ("128,500 TND"), not the two a naive currency formatter assumes.
 *
 * Amounts are accepted in millimes (an integer) rather than as a TND float.
 * Money-as-float is a well-known source of rounding bugs; storing and
 * passing the smallest unit is the standard mitigation, mirroring how many
 * payment APIs represent minor units. Dividing an integer by exactly 1000 in
 * IEEE-754 is safe (no precision loss) for every realistic order amount.
 *
 * `Intl.NumberFormat` is used with explicit fraction-digit bounds rather than
 * left to infer them from the currency code: some minimal ICU builds do not
 * ship complete Tunisian dinar metadata, and a silently-wrong "128,50 TND"
 * on an invoice is a financial-display bug, not a cosmetic one.
 */
const LOCALE_TAG: Record<Locale, string> = {
  fr: 'fr-TN',
  ar: 'ar-TN',
  en: 'en-TN',
};

export function formatMillimesAsTnd(amountMillimes: number, locale: Locale): string {
  if (!Number.isFinite(amountMillimes)) {
    throw new RangeError(
      `formatMillimesAsTnd received a non-finite amount: ${String(amountMillimes)}`,
    );
  }

  const amountTnd = amountMillimes / 1000;

  return new Intl.NumberFormat(LOCALE_TAG[locale], {
    style: 'currency',
    currency: 'TND',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(amountTnd);
}

/** Same formatting, without the currency symbol — for contexts that render "TND" separately. */
export function formatMillimesAsNumber(amountMillimes: number, locale: Locale): string {
  if (!Number.isFinite(amountMillimes)) {
    throw new RangeError(
      `formatMillimesAsNumber received a non-finite amount: ${String(amountMillimes)}`,
    );
  }

  const amountTnd = amountMillimes / 1000;

  return new Intl.NumberFormat(LOCALE_TAG[locale], {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(amountTnd);
}
